import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Eye, MousePointerClick } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface JobStats {
  id: string;
  title: string;
  views: number;
  clicks: number;
  conversion_rate: number;
}

interface TopJobsTableProps {
  jobs: JobStats[];
}

function MobileJobCard({ job, index }: { job: JobStats; index: number }) {
  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-mono text-xs shrink-0">
          #{index + 1}
        </Badge>
        <span className="font-medium text-sm truncate">{job.title}</span>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <Eye className="h-3.5 w-3.5" />
          <span>{job.views}</span>
        </div>
        <div className="flex items-center gap-1">
          <MousePointerClick className="h-3.5 w-3.5" />
          <span>{job.clicks}</span>
        </div>
        <Badge variant={job.conversion_rate > 10 ? "default" : "secondary"} className="ml-auto">
          {job.conversion_rate.toFixed(1)}%
        </Badge>
      </div>
    </div>
  );
}

export function TopJobsTable({ jobs }: TopJobsTableProps) {
  const isMobile = useIsMobile();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance por Vaga</CardTitle>
        <CardDescription>Ranking das vagas com mais engajamento</CardDescription>
      </CardHeader>
      <CardContent>
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhuma vaga publicada ainda
          </div>
        ) : isMobile ? (
          <div className="space-y-2">
            {jobs.map((job, index) => (
              <MobileJobCard key={job.id} job={job} index={index} />
            ))}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vaga</TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Eye className="h-3 w-3" />
                      Views
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <MousePointerClick className="h-3 w-3" />
                      Cliques
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Conversão</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job, index) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          #{index + 1}
                        </Badge>
                        <span className="font-medium">{job.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {job.views}
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {job.clicks}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge 
                        variant={job.conversion_rate > 10 ? "default" : "secondary"}
                      >
                        {job.conversion_rate.toFixed(1)}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
